import {
  CONNECTOR_FAN_LABELS_SCALE,
  ConnectorFan,
  Container,
  Eyebrow,
  ProductTile,
  Section,
} from "@vcn/ui";
import { useContent, type CapModule } from "../content";

function ModulesIntro() {
  const { modulesIntro } = useContent()

  return (
    <Section id="module" className="scroll-mt-20 pb-4 text-center">
      <Container>
        <h2 className="text-3xl leading-tight font-bold text-balance sm:text-4xl lg:text-[2.6rem]">
          <span className="block text-white">{modulesIntro.title}</span>
          <span className="block font-normal text-white/90">
            {modulesIntro.subtitle}
          </span>
        </h2>
      </Container>
    </Section>
  );
}

function ModuleBlock({ module }: { module: CapModule }) {
  // `align` steuert nur das Desktop-Layout – mobil steht der Text immer oben.
  const textLeft = module.align === "left";

  return (
    <Section id={module.id} className="scroll-mt-20 py-12 sm:py-16">
      <Container>
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-x-16">
          <div
            className={
              textLeft
                ? "text-center lg:text-left"
                : "text-center lg:order-2 lg:text-left"
            }
          >
            <Eyebrow as="h3">{module.eyebrow}</Eyebrow>
            <p className="mt-3 text-2xl leading-tight font-bold text-balance text-white sm:text-[1.75rem]">
              {module.title.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </p>
            <p className="mx-auto mt-5 max-w-md text-[0.95rem] leading-relaxed text-pretty text-white/85 lg:mx-0">
              {module.body}
            </p>
          </div>

          {/* Kachel → Fächerlinien → Stichpunkte */}
          <div
            className={[
              // `min-w-0`, damit die Zeile auf schmalen Displays (320 px)
              // nicht breiter wird als die Spalte.
              "flex min-w-0 items-center justify-center gap-2.5 [--fan-h:8rem] sm:gap-5 sm:[--fan-h:12.5rem]",
              textLeft ? "" : "lg:order-1 lg:flex-row-reverse",
            ].join(" ")}
          >
            <ProductTile
              brand="cap"
              label={module.label}
              width="clamp(5rem, 24vw, 11.5rem)"
              decorative
              className="shrink-0"
            />
            {/*
              Breite folgt der Höhe – Seitenverhältnis wie Element 617/618/619.

              Mobil steht die Kachel im Entwurf bei allen drei Modulen links
              und der Fächer öffnet sich nach rechts. Erst ab lg dreht sich
              die Zeile (`lg:flex-row-reverse`) – dann, und nur dann, muss
              auch der Fächer gespiegelt werden.
            */}
            <ConnectorFan
              className={[
                "h-[var(--fan-h)]",
                textLeft ? "" : "lg:-scale-x-100",
              ].join(" ")}
            />
            {/*
              Drei gleich hohe Zeilen, Text jeweils mittig darin: so trifft
              jeder Punkt des Fächers die Mitte seines Stichpunkts – wie im
              Entwurf. Die Liste ragt dafür oben und unten über den Fächer
              hinaus (siehe CONNECTOR_FAN_LABELS_SCALE).
            */}
            <ul
              style={{
                height: `calc(var(--fan-h) * ${CONNECTOR_FAN_LABELS_SCALE})`,
              }}
              className={[
                "grid min-w-0 grid-rows-3 items-center text-sm leading-snug font-bold text-balance text-white sm:text-[0.95rem]",
                textLeft ? "" : "lg:text-right",
              ].join(" ")}
            >
              {module.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </div>
        </div>
      </Container>
    </Section>
  );
}

export function Modules() {
  const { modules } = useContent()

  return (
    <>
      <ModulesIntro />
      {modules.map((module) => (
        <ModuleBlock key={module.id} module={module} />
      ))}
    </>
  );
}
